/* eslint-disable import/no-cycle */
import React, { useState, ComponentType, PropsWithChildren, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  SystemStyleObject,
  theme,
  Text,
} from '@chakra-ui/core';
import { useTranslation } from 'react-i18next';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';

import { TextWithTranslationsT } from '../types';
import useHireFormErrorHandling, { ValidationType, ValueType } from '../hooks/useHireFormErrorHandling';

// --- Types ---

type InitialValueT = Record<string, string> | string;

/** Props received by the HOC from the outside world */
interface WithToggleVisibilityOuterProps<T extends InitialValueT> {
  initialValue: T;
  // path: PathT;
  fieldId: string;
  innerPath: string;
  isAdvanced?: boolean;
  validationType?: ValidationType;
  currentLanguage?: string;
  validate?: (callback: (type: ValueType) => boolean | undefined, value: T, language?: string) => void;
  isDisabled?: boolean;
  placeholder?: string;
  sx?: SystemStyleObject | undefined;
  previewComponent?: (value: string, error?: string, placeholderText?: string) => React.ReactNode;
  completeValue?: TextWithTranslationsT;
}

/** Props that the HOC injects into the WrappedComponent */
interface InjectedProps<T extends InitialValueT>
  extends Pick<WithToggleVisibilityOuterProps<T>, 'placeholder' | 'fieldId' | 'innerPath' | 'isAdvanced' | 'completeValue'> {
  value: string;
  onBlur?: () => void;
}

// 1. Move static styles outside to prevent object recreation
const staticStyles: SystemStyleObject = {
  fontWeight: 'normal',
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
  whiteSpace: 'pre-wrap',
  minHeight: '1.2rem',
  display: 'inline-block',
  fontSize: 'sm',
  position: 'relative',
  zIndex: 0,
};

/**
 * HOC: withToggleVisibility
 * Manages the "View-to-Edit" state.
 * Shows a preview/placeholder by default and swaps to the 'WrappedComponent' (usually an Input) when clicked.
 */
const withToggleVisibility = <T extends InitialValueT, P extends InjectedProps<T>>(WrappedComponent: ComponentType<P>) => {
  type HOCProps = WithToggleVisibilityOuterProps<T> & Omit<P, keyof InjectedProps<T>>;

  const HOC: React.FC<PropsWithChildren<HOCProps>> = (props) => {
    const {
      initialValue,
      sx,
      children,
      previewComponent,
      isDisabled = false,
      placeholder,
      // path,
      validationType,
      currentLanguage,
      validate: validateFn,
      fieldId,
      innerPath,
      isAdvanced,
      completeValue,
      ...restProps
    } = props;

    const { t } = useTranslation('hrFormTemplate');
    const [isEditing, setIsEditing] = useState(false);

    // --- Error Handling ---
    const { hasError, validate, error } = useHireFormErrorHandling({
      targetId: fieldId,
      innerPath,
      validationType,
      isAdvanced: isAdvanced || false,
    });

    // --- Derived State ---

    // Extracts the correct string for the current UI language from the translation object
    const valueForLanguage = useMemo(() => {
      if (isString(initialValue)) return initialValue;
      if (isObject(initialValue) && currentLanguage) {
        return initialValue[currentLanguage] || '';
      }
      return '';
    }, [initialValue, currentLanguage]);

    // Runs validation logic whenever the underlying value changes
    useEffect(() => {
      validateFn?.(validate, initialValue, currentLanguage);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValue]);

    // --- Handlers ---

    const handleEnableEditing = useCallback(() => {
      if (!isDisabled) setIsEditing(true);
    }, [isDisabled]);

    const handleDisableEditing = useCallback(() => {
      setIsEditing(false);
    }, []);

    // --- Component Injection Preparation ---

    const injected: InjectedProps<T> = useMemo(
      () => ({
        value: valueForLanguage || '',
        onBlur: handleDisableEditing,
        // path,
        placeholder,
        innerPath,
        fieldId,
        isAdvanced,
        completeValue,
      }),
      [valueForLanguage, handleDisableEditing, placeholder, innerPath, fieldId, isAdvanced, completeValue],
    );

    // Handles custom preview components (e.g. specialized text renderings)
    const renderCustomPreview = useCallback(
      (value: string, currentError?: string, placeholderText?: string) => {
        if (!previewComponent) return null;

        const previewElement = previewComponent(value, currentError, placeholderText);
        return React.cloneElement(previewElement as React.ReactElement, {
          value,
          error: currentError,
          placeholderText,
          onClick: handleEnableEditing, // Ensure custom preview can trigger edit mode
        });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [previewComponent, handleEnableEditing, error, valueForLanguage, placeholder],
    );

    // --- Render Logic ---

    const dynamicSx = useMemo(
      () => ({
        ...staticStyles,
        textDecoration: isDisabled ? 'none' : 'underline',
        textDecorationColor: hasError ? theme.colors.red[500] : theme.colors.gray[300],
        color: theme.colors.gray[500],
        cursor: isDisabled ? 'not-allowed' : 'text',
        opacity: isDisabled ? 0.6 : 1,
        ...sx,
      }),
      [isDisabled, hasError, sx],
    );

    // EDIT MODE: Return the Input/Component being wrapped
    if (isEditing) {
      return (
        <WrappedComponent {...injected} {...((restProps as unknown) as P)}>
          {children}
        </WrappedComponent>
      );
    }

    // PREVIEW MODE: Show custom preview or the default styled underline text
    return (
      <Box>
        {renderCustomPreview(valueForLanguage, error, placeholder) || (
          <Popover placement="bottom-start" isLazy trigger="hover">
            <PopoverTrigger>
              <Box>
                <Box
                  onClick={handleEnableEditing}
                  sx={dynamicSx}
                  _hover={{
                    _after: {
                      content: '""',
                      position: 'absolute',
                      bottom: '-1px',
                      left: '-5px',
                      width: 'calc(100% + 10px)',
                      height: 'calc(100% + 2px)',
                      background: '#f2ebff',
                      borderRadius: '3px',
                      border: '1px solid #e8dbff',
                      zIndex: -1,
                    },
                  }}
                >
                  {valueForLanguage || placeholder}
                </Box>

                {hasError && (
                  <Text fontSize="xs" color="red.500" mt={1}>
                    {error}
                  </Text>
                )}
              </Box>
            </PopoverTrigger>

            <PopoverContent boxShadow="xl">
              <PopoverHeader fontWeight="semibold" fontSize="sm">
                {t('formBuilder.infoTooltips.label.title')}
              </PopoverHeader>
              <PopoverArrow />
              <PopoverBody>
                <Text fontSize="sm">{t('formBuilder.infoTooltips.label.description')}</Text>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        )}
      </Box>
    );
  };

  HOC.displayName = `withToggleVisibility(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return HOC;
};

export default withToggleVisibility;
